import {
  getAssessmentRequestArchiveCommentDisplay,
  getAssessmentRequestGxpDisplay,
  getAssessmentRequestLocationDisplay,
  getAssessmentRequestMultiSponsorDisplay,
  getAssessmentRequestOnsiteRemoteDisplay,
  getAssessmentRequestTypeDisplay,
  isAssessmentRequestMultiSponsor,
  formatAssessmentHistoryShortDate,
  getAssessmentRequestAuditorDisplay
} from "../../utils/assessmentRequestGridFields";
import {
  sortConfigToApiSortParamsWithFieldMap,
} from "../../utils/myRfiGridRowMapper";
import {
  formatCapabilitiesLabels,
  formatScopeWithAdditionalScopeForExport,
  formatAddedScopeForExport,
} from "../../utils/scopeDisplayUtils";
import { VQA_STATUS_ORDER } from "../../utils/assessmentStatuses";

/**
 * Toggle server-side sort for sponsor/provider `Assessments/Request` vs client-side sort on current page.
 */
export const VQA_LIST_USE_SERVER_SORT = true;

/**
 * Toggle server-side sort for `Assessments/Admin/Request` vs client-side sort on current page.
 */
export const BACKEND_ASSESSMENT_REQUEST_USE_SERVER_SORT = true;

/**
 * First-load sort for Assessment Request grids (sponsor / vendor / admin).
 * Maps to Assessments/Request|Admin/Request `sort_by=request_date` / `sort_direction=desc`.
 */
export const BACKEND_ASSESSMENT_REQUEST_DEFAULT_SORT_CONFIG = {
  requestDate: { direction: "desc" },
};

/**
 * Column keys for Admin Assessment Request grid / sort state (single active column).
 * `sort_by` values for `Assessments/Request` and `Assessments/Admin/Request` (server contract).
 */
export const BACKEND_ASSESSMENT_REQUEST_API_ORDER_BY_FIELD = {
  sponsorName: "sponsor_name",
  vendor: "provider_name",
  assessmentType: "assessment_type",
  gxp: "gxp",
  location: "location",
  onsiteRemote: "vqa_type",
  status: "status",
  multiSponsor: "is_multi_sponsor_assessment",
  auditor: "auditor",
  requestDate: "request_date",
  releasedDate: "released_date",
  archiveComment: "archive_comment",
  requestType: "request_type",
  assessmentDate: "assessment_date",
};

/** Request Type badge ordering: Initial Request -> Joined Request -> Request Pending. */
const REQUEST_TYPE_SORT_ORDER = [
  "Initial Request",
  "Joined Request",
  "Request Pending",
];

export function sortBackendAssessmentRequestConfigToApiSortParams(sortConfig) {
  return sortConfigToApiSortParamsWithFieldMap(
    BACKEND_ASSESSMENT_REQUEST_API_ORDER_BY_FIELD,
    sortConfig
  );
}

function naturalSortCompare(a, b) {
  if (!a && b) return -1;
  if (a && !b) return 1;
  if (!a && !b) return 0;
  const aStr = String(a).trim();
  const bStr = String(b).trim();
  const aParts = aStr.split(/(\d+)/).filter(Boolean);
  const bParts = bStr.split(/(\d+)/).filter(Boolean);
  const minLength = Math.min(aParts.length, bParts.length);
  for (let i = 0; i < minLength; i++) {
    if (!isNaN(aParts[i]) && !isNaN(bParts[i])) {
      const numA = parseInt(aParts[i], 10);
      const numB = parseInt(bParts[i], 10);
      if (numA !== numB) return numA - numB;
    } else if (aParts[i] !== bParts[i]) {
      return aParts[i].localeCompare(bParts[i]);
    }
  }
  return aParts.length - bParts.length;
}

function signedComparison(diff, mult) {
  if (diff === 0) return 0;
  return diff < 0 ? -mult : mult;
}

/**
 * Client-side sort for assessment request rows (when server sort flag is off).
 */
export function sortBackendAssessmentRequestItems(items, sortConfig) {
  if (!items?.length) return [];
  const copy = [...items];
  const keys = Object.keys(sortConfig || {});
  if (keys.length === 0) return copy;

  const key = keys[0];
  const { direction } = sortConfig[key];
  const mult = direction === "asc" ? 1 : -1;

  return copy.sort((a, b) => {
    if (key === "sponsorName") {
      const result = naturalSortCompare(
        (a.sponsor_name || "").toLowerCase(),
        (b.sponsor_name || "").toLowerCase()
      );
      return result * mult;
    }
    if (key === "vendor") {
      const result = naturalSortCompare(
        (a.provider_name || "").toLowerCase(),
        (b.provider_name || "").toLowerCase()
      );
      return result * mult;
    }
    if (key === "assessmentType") {
      const result = naturalSortCompare(
        getAssessmentRequestTypeDisplay(a).toLowerCase(),
        getAssessmentRequestTypeDisplay(b).toLowerCase()
      );
      return result * mult;
    }
    if (key === "gxp") {
      const result = naturalSortCompare(
        getAssessmentRequestGxpDisplay(a).toLowerCase(),
        getAssessmentRequestGxpDisplay(b).toLowerCase()
      );
      return result * mult;
    }
    if (key === "location") {
      const result = naturalSortCompare(
        getAssessmentRequestLocationDisplay(a).toLowerCase(),
        getAssessmentRequestLocationDisplay(b).toLowerCase()
      );
      return result * mult;
    }
    if (key === "onsiteRemote") {
      const result = naturalSortCompare(
        getAssessmentRequestOnsiteRemoteDisplay(a).toLowerCase(),
        getAssessmentRequestOnsiteRemoteDisplay(b).toLowerCase()
      );
      return result * mult;
    }
    if (key === "status") {
      const valueA = a.vqa_request_status || "";
      const valueB = b.vqa_request_status || "";
      const aIndex = VQA_STATUS_ORDER.indexOf(valueA);
      const bIndex = VQA_STATUS_ORDER.indexOf(valueB);
      if (aIndex !== -1 && bIndex !== -1) {
        return (aIndex - bIndex) * mult;
      }
      const cmp = valueA.localeCompare(valueB);
      return cmp * mult;
    }
    if (key === "multiSponsor") {
      const na = Number(isAssessmentRequestMultiSponsor(a));
      const nb = Number(isAssessmentRequestMultiSponsor(b));
      if (na === nb) return 0;
      return na < nb ? -mult : mult;
    }
    if (key === "auditor") {
      const result = naturalSortCompare(
        (a.vqa_auditor_name || "").toLowerCase(),
        (b.vqa_auditor_name || "").toLowerCase()
      );
      return result * mult;
    }
    if (key === "requestDate") {
      const dateA = a.vqa_request_created_On ? new Date(a.vqa_request_created_On) : null;
      const dateB = b.vqa_request_created_On ? new Date(b.vqa_request_created_On) : null;
      if (!dateA && dateB) return mult;
      if (dateA && !dateB) return -mult;
      if (!dateA && !dateB) return 0;
      const diff = dateA - dateB;
      return signedComparison(diff, mult);
    }
    if (key === "archiveComment") {
      const ca = (a?.archive_comment ?? a?.archiveComment ?? "")
        .toString()
        .toLowerCase();
      const cb = (b?.archive_comment ?? b?.archiveComment ?? "")
        .toString()
        .toLowerCase();
      return naturalSortCompare(ca, cb) * mult;
    }
    if (key === "releasedDate") {
      const dateA = a.released_date ? new Date(a.released_date) : null;
      const dateB = b.released_date ? new Date(b.released_date) : null;
      if (!dateA && dateB) return mult;
      if (dateA && !dateB) return -mult;
      if (!dateA && !dateB) return 0;
      const diff = dateA - dateB;
      return signedComparison(diff, mult);
    }
    if (key === "requestType") {
      const indexA = REQUEST_TYPE_SORT_ORDER.indexOf(a.request_type);
      const indexB = REQUEST_TYPE_SORT_ORDER.indexOf(b.request_type);
      const rankA = indexA === -1 ? REQUEST_TYPE_SORT_ORDER.length : indexA;
      const rankB = indexB === -1 ? REQUEST_TYPE_SORT_ORDER.length : indexB;
      return (rankA - rankB) * mult;
    }
    if (key === "assessmentDate") {
      // Unset rows sort last regardless of direction — same shape as requestDate
      // client sort but with the null-last guarantee flipped for descending too.
      const dateA = a.assessment_date ? new Date(a.assessment_date) : null;
      const dateB = b.assessment_date ? new Date(b.assessment_date) : null;
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;
      const diff = dateA - dateB;
      return signedComparison(diff, mult);
    }
    return 0;
  });
}

/** Type filter options (labels aligned with Innovation Lab assessment types; values match substrings in API text). */
export const ASSESSMENT_REQUEST_TYPE_FILTER_OPTIONS = [
  { value: "GCP", label: "GCP (Clinical) Assessment" },
  { value: "GCLP", label: "GCLP (Clinical Laboratory) Assessment" },
  { value: "GLP", label: "GLP (Laboratory) Assessment" },
  { value: "GMP", label: "GMP (Manufacturing) Assessment" },
  { value: "GVP", label: "GVP (Pharmacovigilance) Assessment" },
  { value: "GDP", label: "GDP (Distribution) Assessment" },
  { value: "GAMP", label: "GAMP (Automated Manufacturing) Assessment" },
];

export const ASSESSMENT_REQUEST_ONSITE_REMOTE_FILTER_OPTIONS = [
  { value: "Onsite", label: "Onsite" },
  { value: "Remote", label: "Remote" },
];

export const ASSESSMENT_REQUEST_MULTI_SPONSOR_FILTER_OPTIONS = [
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
];

/**
 * Request Type checkbox filter options (admin-only). Values are the literal
 * backend badge labels (same vocabulary as `item.request_type`), matching how the
 * existing Status filter section reuses its display strings as filter values.
 */
export const REQUEST_TYPE_FILTER_OPTIONS = [
  { value: "Initial Request", label: "Initial Request" },
  { value: "Joined Request", label: "Joined Request" },
  { value: "Request Pending", label: "Request Pending" },
];

/** Matches RequestTypeClassifier.Initial/Joined/Pending on the backend (0/1/2). */
const REQUEST_TYPE_LABEL_TO_CODE = {
  "Initial Request": 0,
  "Joined Request": 1,
  "Request Pending": 2,
};

/**
 * Builds the `request_type_list` int-array param the admin list endpoint expects.
 * Returns `undefined` (not `[]`) when nothing is selected, so callers can omit the key
 * from the request payload entirely rather than sending an empty filter.
 *
 * Request Type MUST stay a server-side filter: unlike Multi-Sponsor, there is
 * deliberately no client-side branch for it in `filterAssessmentRequestItemsByClientFilters`
 * — a client-only filter would narrow the current page without correcting `total_pages`,
 * silently breaking pagination.
 */
export function buildRequestTypeListApiParam(selectedRequestTypeLabels) {
  if (!selectedRequestTypeLabels?.length) return undefined;
  const codes = selectedRequestTypeLabels
    .map((label) => REQUEST_TYPE_LABEL_TO_CODE[label])
    .filter((code) => code !== undefined);
  return codes.length ? codes : undefined;
}

/**
 * Unique React key for a per-sponsor admin grid row. vqa_request_id alone repeats
 * across the originator and joiner rows of one shared assessment.
 */
export function buildAdminRowKey(item) {
  return `${item?.vqa_request_id ?? ""}:${item?.sponsor_id ?? ""}:${
    item?.join_vqa_request_id ?? "originator"
  }`;
}

/**
 * Client-side filters for toolbar sections not covered by Assessments/Admin/Request (current page only).
 * Search text also narrows rows when the API does not yet apply `search_text`.
 */
export function filterAssessmentRequestItemsByClientFilters(
  items,
  selectedIds,
  searchQuery
) {
  if (!items?.length) return [];
  let list = [...items];

  const q = (searchQuery || "").trim().toLowerCase();
  if (q) {
    list = list.filter((item) => {
      const sponsor = (item.sponsor_name || "").toLowerCase();
      const vendor = (item.provider_name || "").toLowerCase();
      const status = (item.vqa_request_status || "").toLowerCase();
      const auditor = (item.vqa_auditor_name || "").toLowerCase();
      const caps = formatCapabilitiesLabels(item.capabilities).toLowerCase();
      const additionalScope = (item.additional_scope || [])
        .join(", ")
        .toLowerCase();
      const type = getAssessmentRequestTypeDisplay(item).toLowerCase();
      const gxp = getAssessmentRequestGxpDisplay(item).toLowerCase();
      const loc = getAssessmentRequestLocationDisplay(item).toLowerCase();
      const onsite = getAssessmentRequestOnsiteRemoteDisplay(item).toLowerCase();
      const archiveNote =
        getAssessmentRequestArchiveCommentDisplay(item).toLowerCase();
      return (
        sponsor.includes(q) ||
        vendor.includes(q) ||
        status.includes(q) ||
        auditor.includes(q) ||
        caps.includes(q) ||
        additionalScope.includes(q) ||
        type.includes(q) ||
        gxp.includes(q) ||
        loc.includes(q) ||
        onsite.includes(q) ||
        archiveNote.includes(q)
      );
    });
  }

  const typeFilters = selectedIds?.type || [];
  if (typeFilters.length > 0) {
    list = list.filter((item) => {
      const t = getAssessmentRequestTypeDisplay(item);
      const tu = t.toUpperCase();
      return typeFilters.some((fv) => tu.includes(String(fv).toUpperCase()));
    });
  }

  const gxpFilters = selectedIds?.gxp || [];
  if (gxpFilters.length > 0) {
    list = list.filter((item) => {
      const g = getAssessmentRequestGxpDisplay(item).toUpperCase();
      return gxpFilters.some((code) => {
        const c = String(code).toUpperCase();
        return g === c || g.includes(c);
      });
    });
  }

  const onsiteFilters = selectedIds?.onsiteRemote || [];
  if (onsiteFilters.length > 0) {
    list = list.filter((item) => {
      const o = getAssessmentRequestOnsiteRemoteDisplay(item);
      return onsiteFilters.some((f) => o === f);
    });
  }

  const auditorFilters = selectedIds?.auditor || [];
  if (auditorFilters.length > 0) {
    list = list.filter((item) => {
      const aid = item?.vqa_auditor_id ?? item?.auditor_id;
      if (aid != null && auditorFilters.includes(aid)) return true;
      return false;
    });
  }

  const msFilters = selectedIds?.multiSponsor || [];
  if (msFilters.length > 0) {
    list = list.filter((item) => {
      const isMs = isAssessmentRequestMultiSponsor(item);
      return msFilters.some((f) => {
        if (f === "Yes") return isMs;
        if (f === "No") return !isMs;
        return false;
      });
    });
  }

  return list;
}

/** Sponsor / vendor assessment request exports (no admin-only columns). */
export const BACKEND_ASSESSMENT_REQUEST_EXPORT_HEADERS = [
  "Sponsor",
  "Vendor",
  "Type",
  "GxP",
  "Scope",
  "Location",
  "Onsite / Remote",
  "Multiple Sponsors Permitted",
  "Auditor",
  "Status",
  "Request Date",
  "Release Date",
  "Archive Comment",
];

/**
 * Admin-only export headers: Request Type, Additional Participating Sponsors and
 * Assessment Date, inserted immediately after Multiple Sponsors Permitted. In the export
 * Assessment Date stays in this group; on screen it sits after Request Date.
 */
const ADMIN_EXTRA_HEADERS_AFTER_MULTI_SPONSOR = [
  "Request Type",
  "Additional Participating Sponsors",
  "Assessment Date",
];

const MULTI_SPONSOR_HEADER_INDEX = BACKEND_ASSESSMENT_REQUEST_EXPORT_HEADERS.indexOf(
  "Multiple Sponsors Permitted"
);

/** Admin export: same as sponsor/vendor, with the 14542 columns spliced in, plus Archive Comment. */
export const BACKEND_ASSESSMENT_REQUEST_EXPORT_HEADERS_ADMIN = [
  ...BACKEND_ASSESSMENT_REQUEST_EXPORT_HEADERS.slice(
    0,
    MULTI_SPONSOR_HEADER_INDEX + 1
  ),
  ...ADMIN_EXTRA_HEADERS_AFTER_MULTI_SPONSOR,
  ...BACKEND_ASSESSMENT_REQUEST_EXPORT_HEADERS.slice(
    MULTI_SPONSOR_HEADER_INDEX + 1
  ),
  "Archive Comment",
];

/**
 * @param {object} item
 * @param {{ includeArchiveComment?: boolean }} [options] — true for admin CSV only.
 */
export function mapBackendAssessmentRequestItemToExportRecord(
  item,
  options = {}
) {
  const { includeArchiveComment = false } = options;
  const auditor = getAssessmentRequestAuditorDisplay(item);
  const type = getAssessmentRequestTypeDisplay(item);
  const gxp = getAssessmentRequestGxpDisplay(item);
  const location = getAssessmentRequestLocationDisplay(item);
  const onsiteRemote = getAssessmentRequestOnsiteRemoteDisplay(item);
  const baseScope = formatScopeWithAdditionalScopeForExport(
    item?.capabilities || item?.Capabilities,
    item?.additional_scope
  );
  const addedScopeText = formatAddedScopeForExport(
    item?.added_scope,
    item?.addon_scope_tags
  );
  const additionalParticipatingSponsorsText = Array.isArray(
    item?.additional_participating_sponsors
  )
    ? item.additional_participating_sponsors
        .filter((s) => s?.sponsor_name)
        .map((s) => `${s.sponsor_name} (${s.role})`)
        .join("; ")
    : "";
  const base = {
    Sponsor: item?.sponsor_name ?? "",
    Vendor: item?.provider_name ?? "",
    Type: type || "",
    "GxP": gxp || "",
    Scope:
      addedScopeText && baseScope
        ? `${baseScope} | ${addedScopeText}`
        : baseScope || addedScopeText,
    Location: location || "",
    "Onsite / Remote": onsiteRemote || "",
    "Multiple Sponsors Permitted": getAssessmentRequestMultiSponsorDisplay(item),
    "Request Type": item?.request_type ?? "",
    "Additional Participating Sponsors": additionalParticipatingSponsorsText,
    "Assessment Date": item?.assessment_date
      ? formatAssessmentHistoryShortDate(item.assessment_date)
      : "",
    Auditor: auditor || "",
    Status: item?.vqa_request_status ?? "",
    "Request Date": item?.vqa_request_created_On
      ? formatAssessmentHistoryShortDate(item.vqa_request_created_On)
      : "",
    "Release Date": item?.released_date
      ? formatAssessmentHistoryShortDate(item.released_date)
      : "",
    "Archive Comment":
      item?.archive_comment ?? item?.Archive_Comment ?? "",
  };
  if (!includeArchiveComment) return base;
  return {
    ...base,
    "Archive Comment": getAssessmentRequestArchiveCommentDisplay(item) || "",
  };
}
